import type { FC } from "react";
import "./CustomChartTooltip.scss";

/** Compatible with Recharts Tooltip `Payload` (`ValueType` may be a scalar or array). */
type ChartTooltipPayloadItem = {
  value?: string | number | Array<string | number> | null;
  dataKey?: string | number;
  name?: string | number;
  payload?: Record<string, unknown>;
};

type CustomChartTooltipProps = {
  active?: boolean;
  payload?: ChartTooltipPayloadItem[];
  label?: string;
  title?: string;
  formatTooltip?: (_value: number, _key: string) => string;
  formatLabel?: (_label: string) => string;
};

const getFormattedFromRow = (
  row: Record<string, unknown> | undefined,
  key: string
): string | undefined => {
  if (!row || !key) {
    return undefined;
  }

  for (const candidate of [row[`${key}Formatted`], row[`${key}FormattedValue`]]) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  return undefined;
};

const CustomChartTooltip: FC<CustomChartTooltipProps> = ({
  active,
  payload,
  label,
  formatTooltip,
  formatLabel,
}) => {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="chart-tooltip-container">
      <div className="chart-tooltip-label">
        {formatLabel ? formatLabel(label ?? "") : label}
      </div>

      {payload.map((item, index) => {
        const rawValue = item?.value;
        const value = Array.isArray(rawValue) ? (rawValue[0] ?? 0) : (rawValue ?? 0);
        const key = String(item?.dataKey ?? "");
        const formattedFromRow = getFormattedFromRow(item?.payload, key);
        const formatted =
          formattedFromRow ??
          (formatTooltip ? formatTooltip(Number(value), key) : `${value}`);

        return (
          <div key={`${key}-${index}`} className="chart-tooltip-value">
            <span className="chart-tooltip-title">{String(item.name ?? "")}:</span>
            <span className="chart-tooltip-number">{formatted}</span>
          </div>
        );
      })}
    </div>
  );
};

export default CustomChartTooltip;
